import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { fetchKlachtList, type KlachtSummary } from '../../lib/klachten';
import {
  klachtStatusColor,
  klachtStatusLabel,
  klachtTypeLabel,
  klantVerwachtLabel,
} from '../../lib/klacht-constants';
import { formatDate } from '../../lib/format';

interface Props {
  klachtId: string;
  klachtNummer?: string | null;
  klachtStatus?: string | null;
}

/**
 * Compacte samenvatting van een klacht — gebruikt in OrderDetailView's
 * rechter zijbalk wanneer een werkorder een `Klacht_Parent__c` heeft. Doet
 * zelf één query op de list-endpoint (efficiënter dan een aparte fetch
 * voor één record) — de zijbalk-card hoeft geen tijdlijn of children.
 *
 * Server-side hebben we al `klachtParentNummer` en `klachtStatus` op de
 * order-detail; die geven we mee als initial data zodat de card meteen
 * iets toont en pas verrijkt wanneer de fetch klaar is.
 */
export function KlachtSummaryCard({ klachtId, klachtNummer, klachtStatus }: Props) {
  const [klacht, setKlacht] = useState<KlachtSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Geen dedicated single-record endpoint — list met search op het
    // klachtnummer (of fallback: alle open klachten) levert dezelfde data.
    fetchKlachtList({ status: 'all', search: klachtNummer ?? undefined, limit: 5 })
      .then((rows) => {
        if (cancelled) return;
        const found = rows.find((r) => r.id === klachtId) ?? rows[0] ?? null;
        setKlacht(found);
      })
      .catch(() => {
        if (!cancelled) setKlacht(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [klachtId, klachtNummer]);

  const status = klacht?.status ?? klachtStatus ?? null;
  const number = klacht?.klachtNummer ?? klachtNummer ?? '…';

  return (
    <Link
      to={`/klachten/${klachtId}`}
      className="block rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 transition px-3 py-2.5"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-red-800 font-semibold text-sm">
          <span aria-hidden="true">⚠</span>
          <span>Klacht {number}</span>
        </div>
        {status && (
          <span
            className={clsx(
              'inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium',
              klachtStatusColor(status)
            )}
          >
            {klachtStatusLabel(status)}
          </span>
        )}
      </div>
      {klacht && (
        <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <dt className="text-slate-500">Type</dt>
          <dd className="text-slate-800 truncate">{klachtTypeLabel(klacht.type)}</dd>
          {klacht.klantVerwacht && (
            <>
              <dt className="text-slate-500">Klant verwacht</dt>
              <dd className="text-slate-800 truncate">
                {klantVerwachtLabel(klacht.klantVerwacht)}
              </dd>
            </>
          )}
          <dt className="text-slate-500">Geopend</dt>
          <dd className="text-slate-800">{formatDate(klacht.datumGeopend)}</dd>
          <dt className="text-slate-500">Dagen open</dt>
          <dd className="text-slate-800 tabular-nums">{klacht.dagenOpen}</dd>
          {klacht.eigenaarName && (
            <>
              <dt className="text-slate-500">Eigenaar</dt>
              <dd className="text-slate-800 truncate">{klacht.eigenaarName}</dd>
            </>
          )}
        </dl>
      )}
      {loading && !klacht && (
        <p className="mt-2 text-xs text-slate-500">Klacht-detail laden…</p>
      )}
      <p className="mt-2 text-xs font-medium text-red-700">Open klacht-detail →</p>
    </Link>
  );
}
